<template>
  <div id="zoomed-gallery" class="fixed left-0 top-0 right-0 bottom-0 pdp-product-image-zoom">
    <swiper-container id="pdpImageZoom" ref="sliderZoom" init="false" lazy="true">
      <swiper-slide
        v-for="(item, index) in items"
        :key="`${item.data.assetId}-${index}`"
        class="grid w-screen h-screen relative place-content-center"
      >
        <img :srcset="getSrcSet(item.data.assetId, {c: 'pad'})" class="swiper-lazy" :alt="altText" loading="lazy" />
        <div v-if="item.data.isAccessorized" class="accessories-disclaimer">
          <span v-if="productType === 'bike'">{{ $t('product.detail.image.accessorized.caption') }}</span>
          <span v-else>{{ $t('product.detail.image.accessorizedAftermarket.caption') }}</span>
        </div>
      </swiper-slide>
    </swiper-container>
    <pdl-button
      tertiary
      icon-only
      large
      icon="close"
      class="absolute z-10 text-gray-60 top-2 right-2 hover:text-gray-100 hover:bg-transparent"
      :label="$t('popup.close')"
      @click="close"
    />
  </div>
</template>
<script>
import {ProductImages} from '@/components/containers/pdp/utils/product-images';
import {PdlButton} from '@pedal/pdl-button';
import {PdpImageZoomSwiperStyles, PdpImageCommonSwiperStyles} from '@/constants/pdp-panel-and-zoom.js';
import {getAllFocusableElements} from '@/utils/accessibility/a11y-utils';

export default {
  name: 'PdpProductImageZoom',
  components: {
    PdlButton,
  },
  props: {
    productType: {
      type: String,
      default: '',
    },
    images: {
      type: Array,
      default: () => [],
    },
    currentImageIndex: {
      type: Number,
      default: 0,
    },
    productName: {
      type: String,
      default: '',
    },
    altText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentSlide: 0,
      focusableElements: 'null',
      firstElement: null,
      lastElement: null,
    };
  },
  computed: {
    items() {
      return this.images.filter((item) => item.type !== 'video');
    },
    zoomedSlider() {
      return this.$refs.sliderZoom;
    },
    swiper() {
      return this.zoomedSlider.swiper;
    },
    zoomedGallery() {
      return this.zoomedSlider.parentElement;
    },
    swiperElements() {
      return this.swiper.el;
    },
  },
  watch: {
    currentSlide(value) {
      this.swiper.slideTo(value);
    },
  },
  mounted() {
    this.startListeningKeyboard();
    this.currentSlide = this.currentImageIndex;
    this.initialiseSwiper();
    this.$nextTick(() => {
      this.enableFocusTrap();
    });
  },
  beforeDestroy() {
    this.disableFocusTrap();
    this.stopListeningKeyboard();
  },
  methods: {
    getSrcSet: (assetId, options = {}) => ProductImages.getSrcSet(assetId, options),
    startListeningKeyboard() {
      this.stopListeningKeyboard();
      document.addEventListener('keydown', this.keyPressed);
    },
    stopListeningKeyboard() {
      document.removeEventListener('keydown', this.keyPressed);
    },
    keyPressed(ev) {
      let nextItemIndex = this.currentSlide;
      if (ev.keyCode === 37) {
        nextItemIndex--;
      } else if (ev.keyCode === 39) {
        nextItemIndex++;
      }

      if (nextItemIndex < 0) {
        nextItemIndex = this.items.length - 1;
      } else if (nextItemIndex >= this.items.length) {
        nextItemIndex = 0;
      }

      this.currentSlide = nextItemIndex;
    },
    close() {
      this.$emit('close');
    },
    initialiseSwiper() {
      const swiperEl = document.querySelector('swiper-container#pdpImageZoom');
      if (!swiperEl) return;
      const params = {
        pagination: {
          clickable: true,
        },
        navigation: true,
        loop: true,
        injectStyles: [...PdpImageCommonSwiperStyles, ...PdpImageZoomSwiperStyles],
      };

      Object.assign(swiperEl, params);

      swiperEl.initialize();
    },
    focusableElementsArray() {
      const closeBtn = Array.from(getAllFocusableElements(this.zoomedGallery));
      const swiperFocusableElements = Array.from(getAllFocusableElements(this.swiperElements));

      const accessibleElements = [...swiperFocusableElements, ...closeBtn];
      return accessibleElements;
    },
    enableFocusTrap() {
      this.focusableElements = this.focusableElementsArray();
      if (this.focusableElements.length) {
        this.firstElement = this.focusableElements[0];
        this.lastElement = this.focusableElements[this.focusableElements.length - 1];
        this.firstElement.focus();
      }
      document.addEventListener('keydown', this.trapFocus);
    },
    disableFocusTrap() {
      document.removeEventListener('keydown', this.trapFocus);
    },
    trapFocus(event) {
      if (event.key === 'Tab') {
        if (event.shiftKey && this.swiperActiveElement() === this.firstElement) {
          event.preventDefault();
          this.lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === this.lastElement) {
          event.preventDefault();
          this.firstElement.focus();
        }
      } else if (event.key === 'Escape') {
        this.close();
      }
    },
    swiperActiveElement() {
      return this.swiperElements.parentNode.activeElement;
    },
  },
};
</script>
<style lang="scss" scoped>
.pdp-product-image-zoom {
  swiper-container::part(button-prev),
  swiper-container::part(button-next) {
    color: var(--gray-60);

    @media (hover: hover) {
      &:hover {
        color: var(--gray-100);
      }
    }
  }

  swiper-slide img {
    padding-left: 10vw;
    padding-right: 10vw;
  }
}
</style>
